<template>
  <nav class="navbar navbar-expand navbar-light navbar-bg">
    <a class="sidebar-toggle d-flex p-2" @click.prevent="hideLeftMenu">
      <i class="hamburger align-self-center"></i>
    </a>
    <div class="navbar-collapse collapse">
      <ul class="navbar-nav navbar-align">
        <!-- <li class="nav-item dropdown">
          <notification-dropdown></notification-dropdown>
        </li> -->
        <!-- <li class="nav-item dropdown">
          <message-dropdown></message-dropdown>
        </li> -->
        <li
          v-if="sessionExpiresAt && !sessionExpired"
          class="nav-item d-flex align-items-center h-100 px-3 session-countdown"
        >
          <span
            class="session-countdown-text"
            :class="{ 'session-countdown-text--soon': isExpiringSoon }"
          >
            Session expires in {{ formattedTimeLeft }}
          </span>
          <span class="session-countdown-hint">
            (refreshes automatically)
          </span>
        </li>
        <li class="nav-item d-flex align-items-center h-100">
          <token-expiration-warning
            v-if="tokenExpirationWarning.show"
            :time-left="tokenExpirationWarning.timeLeft"
            @refresh-session="handleRefreshSession"
            class="h-100 d-flex align-items-center"
          />
        </li>
        <li class="nav-item dropdown">
          <user-dropdown></user-dropdown>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script>
// import NotificationDropDown from "@/components/notification-dropdown.vue";
// import MessageDropDown from "@/components/message-dropdown.vue";
import UserDropDown from "@/components/user-dropdown.vue";
import TokenExpirationWarning from "@/components/token-expiration-warning.vue";
import ToastMixin from "@/mixin/toast-mixin";
import { eventBus } from "@/services/event-bus";
import { mapState, mapActions } from "vuex";

// Renew the access token this long before it expires, rather than waiting for
// it to lapse. The access token is short-lived (~10 min); with refresh-token
// rotation it is meant to be renewed silently, NOT to end the session.
const SESSION_RENEW_LEEWAY_MS = 60 * 1000;
// If a renewed access token isn't a decodable JWT, re-check after this long so
// the countdown can't busy-loop on renewal.
const SESSION_RENEW_RECHECK_MS = 5 * 60 * 1000;

export default {
  mixins: [ToastMixin],
  data: function() {
    return {
      // Absolute session expiry (ms epoch) decoded from the Auth0 token `exp`.
      sessionExpiresAt: null,
      // Remaining time until expiry (ms), recomputed every second.
      remainingMs: 0,
      // Guards against firing the expiry handler / toast more than once.
      sessionExpired: false,
      // Guards against overlapping silent-renewal attempts.
      isRenewing: false,
      sessionCountdownInterval: null
    };
  },
  components: {
    // "notification-dropdown": NotificationDropDown,
    // "message-dropdown": MessageDropDown,
    "user-dropdown": UserDropDown,
    "token-expiration-warning": TokenExpirationWarning
  },
  computed: {
    ...mapState(["tokenExpirationWarning"]),
    formattedTimeLeft() {
      const totalSeconds = Math.max(0, Math.floor(this.remainingMs / 1000));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const pad = n => n.toString().padStart(2, "0");
      return hours > 0
        ? `${hours}:${pad(minutes)}:${pad(seconds)}`
        : `${minutes}:${pad(seconds)}`;
    },
    isExpiringSoon() {
      // Highlight the countdown in the final minute.
      return this.remainingMs > 0 && this.remainingMs <= 60000;
    }
  },
  methods: {
    ...mapActions(["refreshToken"]),
    hideLeftMenu() {
      eventBus.emit("toggleLeftMenuEvent");
    },
    async handleRefreshSession() {
      try {
        // Force-refresh the access token via the refresh token (cacheMode "off"
        // inside the action), then recompute the deadline from the new token.
        const ok = await this.refreshToken({
          auth0Client: this.$auth.getClient()
        });
        if (!ok) {
          throw new Error("Token refresh failed");
        }

        // Read the freshly-issued access token to derive the new expiry.
        const { accessToken } = await this.$auth.getTokens();
        const exp = this.decodeTokenExp(accessToken);
        const newExpirationTime = exp ? exp * 1000 - Date.now() : 0;

        // Re-sync the visible countdown with the renewed session.
        if (exp) {
          this.sessionExpiresAt = exp * 1000;
          this.sessionExpired = false;
          this.updateRemaining();
          this.clearSessionCountdown();
          this.sessionCountdownInterval = setInterval(
            this.tickSessionCountdown,
            1000
          );
        }

        // Return the new expiration time to update the warning component.
        return { newExpirationTime };
      } catch (error) {
        console.error("Failed to refresh token:", error);
        throw error;
      }
    },
    async initSessionCountdown() {
      try {
        // Use the ACCESS token, not the id token: the access token is what
        // governs the API session and expires in ~10 minutes, whereas the id
        // token has a much longer lifetime (hours). Decoding the id token made
        // the countdown show hours instead of the real session length.
        // Prefer the token already in the store; fall back to Auth0 for the
        // case where Vuex was reset (e.g. a hard refresh of a /secure page).
        let accessToken = this.$store.getters.accessToken;
        if (!accessToken) {
          const tokens = await this.$auth.getTokens();
          accessToken = tokens && tokens.accessToken;
        }

        const exp = this.decodeTokenExp(accessToken);
        if (!exp) {
          // Expiry unknown — leave the countdown hidden rather than guess.
          return;
        }

        this.sessionExpiresAt = exp * 1000;
        this.updateRemaining();
        if (this.remainingMs <= SESSION_RENEW_LEEWAY_MS) {
          // Already at/near expiry on load — renew instead of logging out.
          this.renewSession();
        }
        this.sessionCountdownInterval = setInterval(
          this.tickSessionCountdown,
          1000
        );
      } catch (error) {
        // getTokens() already clears storage and redirects on a dead session.
        console.error("Failed to initialize session countdown:", error);
      }
    },
    tickSessionCountdown() {
      this.updateRemaining();
      // Don't log out when the access token lapses — renew it. The session only
      // ends when that silent renewal fails (refresh token gone/expired).
      if (this.remainingMs <= SESSION_RENEW_LEEWAY_MS) {
        this.renewSession();
      }
    },
    async renewSession() {
      if (this.isRenewing) return;
      const auth0Client = this.$auth.getClient();
      if (!auth0Client) return;
      this.isRenewing = true;
      try {
        // Force a refresh-token exchange for a brand-new access token.
        const accessToken = await auth0Client.getTokenSilently({
          cacheMode: "off"
        });
        const exp = this.decodeTokenExp(accessToken);
        // Reset the countdown to the renewed token's expiry (or a short
        // re-check window if the token isn't a decodable JWT).
        this.sessionExpiresAt = exp
          ? exp * 1000
          : Date.now() + SESSION_RENEW_RECHECK_MS;
        this.updateRemaining();
      } catch (error) {
        // Refresh token is gone/expired/revoked — the session is truly over.
        console.error("Silent token renewal failed:", error);
        this.handleSessionExpired();
      } finally {
        this.isRenewing = false;
      }
    },
    handleVisibilityChange() {
      // While the tab is hidden the 1s interval is throttled/paused, so the
      // displayed countdown can be stale on return. Recompute immediately (and
      // expire right away if the deadline already passed while hidden).
      if (document.visibilityState === "visible") {
        this.tickSessionCountdown();
      }
    },
    updateRemaining() {
      this.remainingMs = Math.max(0, this.sessionExpiresAt - Date.now());
    },
    async handleSessionExpired() {
      if (this.sessionExpired) return;
      this.sessionExpired = true;
      this.clearSessionCountdown();

      this.displayMessage(
        "warning",
        "Your session has expired. Redirecting to login…"
      );

      // Clear auth state (also stops the store's token-expiration check).
      await this.$store.dispatch("logout");

      // Brief pause so the toast is visible before the route changes.
      setTimeout(() => {
        if (this.$route.path !== "/login") {
          this.$router.push("/login");
        }
      }, 1500);
    },
    decodeTokenExp(token) {
      if (!token || typeof token !== "string") return null;
      try {
        const payload = token.split(".")[1];
        if (!payload) return null;
        const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
        const claims = JSON.parse(atob(normalized));
        return typeof claims.exp === "number" ? claims.exp : null;
      } catch (error) {
        console.error("Failed to decode token expiration:", error);
        return null;
      }
    },
    clearSessionCountdown() {
      if (this.sessionCountdownInterval) {
        clearInterval(this.sessionCountdownInterval);
        this.sessionCountdownInterval = null;
      }
    }
  },
  mounted() {
    this.initSessionCountdown();
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  },
  beforeDestroy() {
    this.clearSessionCountdown();
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";
@import "@/assets/scss/navbar.scss";
@import "@/assets/scss/avatar.scss";
@import "@/assets/scss/hamburger.scss";

.navbar {
  height: 64px;

  @media (min-width: 1px) and (max-width: #{map-get($grid-breakpoints, 'lg') - .02px}) {
    position: sticky;
    top: 0;
    z-index: 1030;
  }
}

.nav-item {
  height: 100%;
}

.session-countdown-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6c757d;
  white-space: nowrap;
}

.session-countdown-text--soon {
  color: #dc2626;
  font-weight: 600;
}

.session-countdown-hint {
  margin-left: 0.375rem;
  font-size: 0.75rem;
  font-style: italic;
  color: #9ca3af;
  white-space: nowrap;
}
</style>
